import { useCallback, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import axios from "../../Uri";
import "./dailySummary.css";

const BUILDING_PREFIX_BY_ID = {
  1: "Sri Nilayam ",
  2: "Vasundara Nilayam",
  3: "Sree Kala Nilayam",
  4: "Ananda Nilayam",
  5: "Sri Sankara Nilayam",
  6: "Vijay Lakshmi Nilayam",
  7: "Durga Nilayam",
};
const BUILDING_OPTIONS = [1, 2, 3, 4, 5, 6, 7].map((id) => ({
  value: id,
  label: `${BUILDING_PREFIX_BY_ID[id]} building ${id}`,
}));

const CHECK_IN_COLUMNS = [
  { key: "id", label: "ID" },
  { key: "checkInDate", label: "Check-in date" },
  { key: "firstName", label: "First name" },
  { key: "bedId", label: "Bed ID" },
  { key: "occupancyType", label: "Occupancy type" },
  { key: "amountPaid", label: "Amount paid" },
];

const CHECK_OUT_COLUMNS = [
  { key: "id", label: "ID" },
  { key: "checkInDate", label: "Check-in date" },
  { key: "firstName", label: "First name" },
  { key: "bedId", label: "Bed ID" },
  { key: "occupancyType", label: "Occupancy type" },
  { key: "checkOutDate", label: "Check-out date" },
];

const TRANSACTION_COLUMNS = [
  { key: "id", label: "ID" },
  { key: "guestId", label: "Guest ID" },
  { key: "transactionId", label: "Transaction ID" },
  { key: "transactionDate", label: "Transaction date" },
  { key: "paymentPurpose", label: "Payment purpose" },
  { key: "amountPaid", label: "Amount paid" },
  {key: "bedId", label: "BED ID"},
];

export default function DailySummary() {
  const [buildingId, setBuildingId] = useState(4);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const printRef = useRef(null);

  const load = useCallback(async () => {
    setError("");
    setData(null);
    setLoading(true);
    try {
      const { data: res } = await axios.get(`/payment/daily-summary`, {
        params: {
          buildingId,
          date: date || undefined,
        },
      });
      setData(res);
    } catch (e) {
      setError(e.response?.data?.message ?? e.message ?? "Request failed");
    } finally {
      setLoading(false);
    }
  }, [buildingId, date]);

  const exportPdf = async () => {
    const node = printRef.current;
    if (!node) return;

    const canvas = await html2canvas(node, {
      scale: 2,
      useCORS: true,
      logging: false,
    });
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    const fname = `daily-summary-building-${data?.buildingId ?? buildingId}-${data?.date ?? date}.pdf`;
    pdf.save(fname);
  };

  const checkIns = data?.checkInsToday ?? data?.checkIns ?? [];
  const checkOuts = data?.checkOutsToday ?? data?.checkOuts ?? [];
  const txs = data?.transactionsToday ?? data?.transactions ?? [];

  return (
   <div className="ds">
      <div className="ds-page">
        <h1 className="ds-title">Daily building summary</h1>

        <div className="ds-toolbar">
          <label className="ds-field">
            <span className="ds-label">Building</span>
            <select
              className="ds-select"
              value={buildingId}
              onChange={(e) => setBuildingId(Number(e.target.value))}
            >
              {BUILDING_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="ds-hint">API parameter: buildingId={buildingId}</span>
          </label>
          
          <label className="ds-field">
            <span className="ds-label">Date</span>
            <input
              className="ds-input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </label>
          <div className="ds-actions">
            <button
              type="button"
              className="ds-btn ds-btn--primary"
              onClick={load}
              disabled={loading}
            >
              {loading ? "Loading…" : "Load summary"}
            </button>
            <button
              type="button"
              className="ds-btn ds-btn--secondary"
              onClick={exportPdf}
              disabled={!data}
            >
              Export PDF
            </button>
          </div>
        </div>

        {error ? <p className="ds-error" role="alert">{error}</p> : null}

        <div ref={printRef} className="ds-card">
          <header className="ds-card-header">
            <h2 className="ds-card-title">Daily summary</h2>
            {data ? (
              <p className="ds-meta">
                Building <strong>{data.buildingId}</strong> · Date <strong>{data.date}</strong>
              </p>
            ) : null}
          </header>

          {!data && !loading ? (
            <p className="ds-placeholder">
              Load a summary to see check-ins, check-outs, and transactions.
            </p>
          ) : null}

          {data ? (
            <>
              <Section
                variant="checkins"
                title={`Check-ins (${checkIns.length})`}
                columns={CHECK_IN_COLUMNS}
                rows={checkIns}
              />
              <Section
                variant="checkouts"
                title={`Check-outs (${checkOuts.length})`}
                columns={CHECK_OUT_COLUMNS}
                rows={checkOuts}
              />
              <Section
                variant="transactions"
                title={`Transactions (${txs.length})`}
                columns={TRANSACTION_COLUMNS}
                rows={txs}
              />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Section({ variant, title, columns, rows }) {
  const sectionClass = `ds-section ds-section--${variant}`;

  if (!rows?.length) {
    return (
      <section className={sectionClass}>
        <h3 className="ds-section-title">{title}</h3>
        <p className="ds-empty">No records.</p>
      </section>
    );
  }

  return (
    <section className={sectionClass}>
      <h3 className="ds-section-title">{title}</h3>
      <div className="ds-table-wrap">
        <table className="ds-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.id ?? row.transactionId ?? i}>
                {columns.map((col) => (
                  <td key={col.key}>{formatCell(row[col.key])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function formatCell(v) {
  if (v == null) return "";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
